// This file was auto-generated by @insertish/oapi!
import Axios, { AxiosRequestConfig } from "axios";
import defaultsDeep from "lodash.defaultsdeep";

import { defaultBaseURL } from "./baseURL";
import { pathResolve, queryParams } from "./params";
import type { APIRoutes } from "./routes";

export * from "./types";
export type { APIRoutes } from "./routes";

type Methods = APIRoutes["method"];
type PickRoutes<Method extends Methods> = APIRoutes & { method: Method };

type GetRoutes = PickRoutes<"get">;
type PatchRoutes = PickRoutes<"patch">;
type PutRoutes = PickRoutes<"put">;
type DeleteRoutes = PickRoutes<"delete">;
type PostRoutes = PickRoutes<"post">;

// Per-method response unions. "& object" drops the non-object members
// (plain-text routes, undefined) that a caller cannot use directly.
type GetResponse = GetRoutes["response"] & object;
type PatchResponse = PatchRoutes["response"] & object;
type PutResponse = PutRoutes["response"] & object;
type DeleteResponse = DeleteRoutes["response"] & object;
type PostResponse = PostRoutes["response"] & object;

/**
 * Get the specific path name of any given path.
 * @param anyPath Any path
 * @returns Specific path
 */
export function getPathName(anyPath: string) {
    const segments = anyPath.split("/");

    const list =
        (pathResolve as unknown as Record<string, (string | [string])[]>)[
            (segments.length - 1).toString()
        ] || [];
    for (const entry of list) {
        let i = 1;
        // eslint-disable-next-line prefer-const
        let copy = [...segments];
        for (i; i < segments.length; i++) {
            if (Array.isArray(entry[i - 1])) {
                copy[i] = entry[i - 1];
                continue;
            } else if (entry[i - 1] !== segments[i]) break;
        }

        if (i === segments.length) return copy.join("/");
    }
}

/**
 * Client configuration options
 */
export interface Options {
    /**
     * Base URL of the Revolt node
     */
    baseURL: string;
    /**
     * Additional headers to apply to requests
     */
    headers?: Record<string, unknown>;
    /**
     * Authentication used for requests
     */
    authentication: {
        rauth?: string | undefined;
        revolt?: { token: string } | string | undefined;
        headers?: Record<string, unknown>;
    };
}

/**
 * Request options
 */
export interface RequestOptions {
    /**
     * The base URL used for this request
     */
    baseURL?: string;
    /**
     * The type of response given
     */
    responseType?: "blob" | "json" | "text" | "arrayBuffer";
    /**
     * Headers to apply for the request
     */
    headers?: Record<string, unknown>;
}

/**
 * API Client
 */
export class API {
    private baseURL: Options["baseURL"];
    private authentication: Options["authentication"];

    constructor({ baseURL, authentication }: Partial<Options> = {}) {
        this.baseURL = baseURL || defaultBaseURL;
        this.authentication = authentication || {};
    }

    /**
     * Generate authentication options.
     */
    get auth(): AxiosRequestConfig {
        if (this.authentication.rauth) {
            if (typeof this.authentication.rauth === "string") {
                return {
                    headers: {
                        "X-Session-Token": this.authentication.rauth,
                    },
                };
            }
        } else if (this.authentication.revolt) {
            switch (typeof this.authentication.revolt) {
                case "string": {
                    return {
                        headers: {
                            "X-Bot-Token": this.authentication.revolt,
                        },
                    };
                }
                case "object": {
                    return {
                        headers: {
                            "X-Session-Token": this.authentication.revolt.token,
                        },
                    };
                }
            }
        }

        return {};
    }

    /**
     * Generate config to pass through to API.
     */
    get config(): AxiosRequestConfig {
        return {
            baseURL: this.baseURL,
            ...this.auth,
        };
    }

    /**
     * Send any arbitrary request.
     * @param method HTTP Method
     * @param path Path
     * @param params Body or Query Parameters
     * @param config Axios configuration
     * @returns Request response data (callers use the typed get/patch/put/delete/post methods)
     */
    req(
        method: Methods,
        path: string,
        params?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<unknown> {
        let query, body;
        const named = getPathName(path);

        // If we are aware of this route and params is a plain object, then match the parameters given.
        if (
            named &&
            typeof params === "object" &&
            params !== null &&
            !Array.isArray(params)
        ) {
            const route = queryParams[named as keyof typeof queryParams];
            const allowed_query = (
                route as unknown as Record<Methods, string[]>
            )[method];

            // Map each parameter to the correct object.
            for (const parameter of Object.keys(params)) {
                if (allowed_query?.includes(parameter)) {
                    query = {
                        ...(query || {}),
                        [parameter]: (params as Record<string, unknown>)[
                            parameter
                        ],
                    };
                } else {
                    body = {
                        ...(body || {}),
                        [parameter]: (params as Record<string, unknown>)[
                            parameter
                        ],
                    };
                }
            }
        }

        const finalData = Array.isArray(params) ? params : body;

        return Axios(
            path,
            defaultsDeep(
                {
                    method,
                    params: query,
                    data: finalData,
                },
                defaultsDeep(config, this.config),
            ),
        ).then((res) => res.data);
    }

    /**
     * Send HTTP GET request.
     * @param path Path — must be a known get route
     * @param params Query parameters (matched against the route at runtime)
     * @param config Axios configuration
     * @returns Typed Response Data (union of this method's object responses; narrow with a cast)
     */
    get<Path extends GetRoutes["path"]>(
        path: Path,
        params?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<GetResponse>;

    get(
        path: string,
        params?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<unknown> {
        return this.req("get", path, params, config);
    }

    /**
     * Send HTTP PATCH request.
     * @param path Path — must be a known patch route
     * @param params Body or query parameters (matched against the route at runtime)
     * @param config Axios configuration
     * @returns Typed Response Data (union of this method's object responses; narrow with a cast)
     */
    patch<Path extends PatchRoutes["path"]>(
        path: Path,
        params?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<PatchResponse>;

    patch(
        path: string,
        params?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<unknown> {
        return this.req("patch", path, params, config);
    }

    /**
     * Send HTTP PUT request.
     * @param path Path — must be a known put route
     * @param params Body or query parameters (matched against the route at runtime)
     * @param config Axios configuration
     * @returns Typed Response Data (union of this method's object responses; narrow with a cast)
     */
    put<Path extends PutRoutes["path"]>(
        path: Path,
        params?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<PutResponse>;

    put(
        path: string,
        params?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<unknown> {
        return this.req("put", path, params, config);
    }

    /**
     * Send HTTP DELETE request.
     * @param path Path — must be a known delete route
     * @param params Body or query parameters (matched against the route at runtime)
     * @param config Axios configuration
     * @returns Typed Response Data (union of this method's object responses; narrow with a cast)
     */
    delete<Path extends DeleteRoutes["path"]>(
        path: Path,
        params?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<DeleteResponse>;

    delete(
        path: string,
        params?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<unknown> {
        return this.req("delete", path, params, config);
    }

    /**
     * Send HTTP POST request.
     * @param path Path — must be a known post route
     * @param params Body or query parameters (matched against the route at runtime)
     * @param config Axios configuration
     * @returns Typed Response Data (union of this method's object responses; narrow with a cast)
     */
    post<Path extends PostRoutes["path"]>(
        path: Path,
        params?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<PostResponse>;

    post(
        path: string,
        params?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<unknown> {
        return this.req("post", path, params, config);
    }
}
